import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FolderKanban, Receipt, FileText, Settings, LogOut, Truck, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/leads", label: "Leads", icon: Users, testId: "nav-leads" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testId: "nav-projects" },
  { to: "/vendors", label: "Vendors", icon: Truck, testId: "nav-vendors" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardList, testId: "nav-purchase-orders" },
  { to: "/quotations", label: "Quotations", icon: FileText, testId: "nav-quotations" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

export default function AppLayout() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-anvi-50">
      <aside className="w-[250px] bg-anvi-900 text-anvi-50 flex flex-col" data-testid="app-sidebar">
        <div className="px-6 py-8 border-b border-anvi-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full border border-anvi-terracotta/60 flex items-center justify-center font-heading text-lg text-white" data-testid="sidebar-monogram">
              {(tenant?.name || "A").trim()[0]}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.25em] uppercase text-anvi-500">Interior Firm</div>
              <div className="font-heading text-lg leading-tight truncate" data-testid="sidebar-brand-name">{tenant?.name || "Anvi Signature"}</div>
            </div>
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-anvi-500 flex items-center gap-2">
            <span>{tenant?.address || "Nagpur, Maharashtra"}</span>
          </div>
          <div className="mt-3 pt-3 border-t border-anvi-800 text-[9px] tracking-[0.2em] uppercase text-anvi-500">
            Powered by InteriorOS
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-anvi-terracotta text-white"
                    : "text-anvi-300 hover:bg-anvi-800 hover:text-white"
                }`
              }
            >
              <item.icon size={18} strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-anvi-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-anvi-terracotta flex items-center justify-center text-white font-semibold text-sm">
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-anvi-400 truncate">{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-anvi-400 hover:text-white p-2 rounded-md hover:bg-anvi-800 transition-colors"
              title="Logout"
            >
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 relative min-w-0">
        <div className="sticky top-0 z-40 flex justify-end px-6 py-3 bg-anvi-50/80 backdrop-blur-md border-b border-anvi-100" data-testid="topbar-notifications">
          <NotificationBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
